import { useState, useEffect, useRef } from 'react';
import { X, UserPlus, Shield, ShieldAlert, Trash2, Edit, Save } from 'lucide-react';
import {
  collection,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  query,
  orderBy,
} from 'firebase/firestore';
import { db, appId } from '@/lib/firebase';
import { AdminView, ASSIGNABLE_ADMIN_MODULES } from './AdminSidebar';

interface AdminData {
  id: string;
  role: 'root' | 'admin' | 'moderator';
  /** Module-specific access for non-root admins. When set, sidebar shows only these modules. */
  allowedModules?: AdminView[];
  email: string;
  displayName: string;
  photoUrl: string | null;
  isActive: boolean;
  createdAt: any;
  createdBy: string | null;
  updatedAt: any;
  updatedBy: string | null;
}

interface AdminUserManagementProps {
  currentUserId: string;
  currentUserRole: 'root' | 'admin' | 'moderator';
  onClose?: () => void; // Optional for page mode
}

export const AdminUserManagement = ({
  currentUserId,
  currentUserRole,
  onClose,
}: AdminUserManagementProps) => {
  const [admins, setAdmins] = useState<AdminData[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminName, setNewAdminName] = useState('');
  const [newAdminModules, setNewAdminModules] = useState<AdminView[]>([]);
  const [editingRootAdmin, setEditingRootAdmin] = useState(false);
  const [rootAdminEmail, setRootAdminEmail] = useState('');
  const [rootAdminName, setRootAdminName] = useState('');
  const [loading, setLoading] = useState(false);

  // Inline editing for non-root admins
  const [editingAdminId, setEditingAdminId] = useState<string | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [editName, setEditName] = useState('');
  const [editModules, setEditModules] = useState<AdminView[]>([]);

  // Fetch admins list
  const unsubRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    // Clean up any existing listener first
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
    
    const adminsRef = collection(db, 'artifacts', appId, 'admins');
    const adminsQuery = query(adminsRef, orderBy('createdAt', 'asc'));
    
    unsubRef.current = onSnapshot(adminsQuery, (snapshot) => {
      const adminsList: AdminData[] = [];
      snapshot.forEach((doc) => {
        adminsList.push({
          id: doc.id,
          ...doc.data(),
        } as AdminData);
      });
      setAdmins(adminsList);
      
      // Set root admin data for editing
      const rootAdmin = adminsList.find((admin) => admin.role === 'root');
      if (rootAdmin && !editingRootAdmin) {
        setRootAdminEmail(rootAdmin.email);
        setRootAdminName(rootAdmin.displayName);
      }
    }, (error) => {
      // Only log non-target-id errors to avoid spam
      if (error.code !== 'failed-precondition' && !error.message.includes('Target ID already exists')) {
        console.error('[AdminUserManagement] Error fetching admins:', error);
      }
    });

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [editingRootAdmin]);

  const handleAddAdmin = async () => {
    if (!newAdminEmail.trim()) {
      alert('Please enter an email address');
      return;
    }
    if (newAdminModules.length === 0) {
      alert('Please select at least one module the admin can access.');
      return;
    }

    setLoading(true);
    try {
      const adminsRef = collection(db, 'artifacts', appId, 'admins');
      // Use the email as the document ID so Firestore rules can look up the admin
      // by email before they sign in for the first time (before UID-based migration).
      const adminDocRef = doc(adminsRef, newAdminEmail.trim());

      await setDoc(adminDocRef, {
        role: 'admin',
        allowedModules: newAdminModules,
        email: newAdminEmail.trim(),
        displayName: newAdminName.trim() || newAdminEmail.split('@')[0],
        photoUrl: null,
        isActive: true,
        createdAt: serverTimestamp(),
        createdBy: currentUserId,
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      });

      setNewAdminEmail('');
      setNewAdminName('');
      setNewAdminModules([]);
      setShowAddForm(false);
      alert(`Admin added successfully. They must sign in with Google to access the selected admin modules.`);
    } catch (error) {
      console.error('[AdminUserManagement] Error adding admin:', error);
      alert('Failed to add admin. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (id: AdminView) => {
    setNewAdminModules((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );
  };

  const selectAllModules = () => {
    setNewAdminModules(ASSIGNABLE_ADMIN_MODULES.map((m) => m.id));
  };

  const clearAllModules = () => {
    setNewAdminModules([]);
  };

  const startEditingAdmin = (admin: AdminData) => {
    setEditingAdminId(admin.id);
    setEditEmail(admin.email);
    setEditName(admin.displayName);
    setEditModules(admin.allowedModules ?? []);
    setShowAddForm(false);
  };

  const cancelEditingAdmin = () => {
    setEditingAdminId(null);
    setEditEmail('');
    setEditName('');
    setEditModules([]);
  };

  const toggleEditModule = (id: AdminView) => {
    setEditModules((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );
  };

  const handleSaveAdmin = async (admin: AdminData) => {
    if (!editEmail.trim()) {
      alert('Email cannot be empty');
      return;
    }
    if (editModules.length === 0) {
      alert('Please select at least one module.');
      return;
    }

    setLoading(true);
    try {
      const adminRef = doc(db, 'artifacts', appId, 'admins', admin.id);
      await updateDoc(adminRef, {
        email: editEmail.trim(),
        displayName: editName.trim() || editEmail.split('@')[0],
        allowedModules: editModules,
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      });
      cancelEditingAdmin();
      alert('Admin updated successfully');
    } catch (error) {
      console.error('[AdminUserManagement] Error updating admin:', error);
      alert('Failed to update admin. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (admin: AdminData) => {
    if (admin.role === 'root') {
      alert('Cannot deactivate the root admin');
      return;
    }

    setLoading(true);
    try {
      const adminRef = doc(db, 'artifacts', appId, 'admins', admin.id);
      await updateDoc(adminRef, {
        isActive: !admin.isActive,
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      });
      alert(`Admin ${!admin.isActive ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error toggling admin status:', error);
      alert('Failed to update admin status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAdmin = async (admin: AdminData) => {
    if (admin.role === 'root') {
      alert('Cannot delete the root admin');
      return;
    }

    if (!confirm(`Are you sure you want to delete admin "${admin.displayName}"? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      const adminRef = doc(db, 'artifacts', appId, 'admins', admin.id);
      await deleteDoc(adminRef);
      alert('Admin deleted successfully');
    } catch (error) {
      console.error('Error deleting admin:', error);
      alert('Failed to delete admin. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRootAdmin = async () => {
    const rootAdmin = admins.find((admin) => admin.role === 'root');
    if (!rootAdmin) {
      alert('Root admin not found');
      return;
    }

    if (!rootAdminEmail.trim()) {
      alert('Email cannot be empty');
      return;
    }

    setLoading(true);
    try {
      const adminRef = doc(db, 'artifacts', appId, 'admins', rootAdmin.id);
      await updateDoc(adminRef, {
        email: rootAdminEmail.trim(),
        displayName: rootAdminName.trim() || rootAdminEmail.split('@')[0],
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      });

      setEditingRootAdmin(false);
      alert('Root admin updated successfully');
    } catch (error) {
      console.error('Error updating root admin:', error);
      alert('Failed to update root admin. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Only root admin can access this component
  if (currentUserRole !== 'root') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Access Denied</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
          <p className="text-gray-700 mb-4">
            Only the root administrator can access this page.
          </p>
          <button
            onClick={onClose}
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const isModalMode = !!onClose;

  const pageContent = (
    <>
      {/* Root Admin Section */}
      <div className={`p-6 ${isModalMode ? 'border-b border-gray-200' : 'mb-6'} bg-blue-50 rounded-lg`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <ShieldAlert className="text-blue-600" size={20} />
              Root Administrator
            </h3>
            {!editingRootAdmin && (
              <button
                onClick={() => setEditingRootAdmin(true)}
                className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm"
              >
                <Edit size={16} />
                Edit
              </button>
            )}
          </div>

          {editingRootAdmin ? (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={rootAdminEmail}
                  onChange={(e) => setRootAdminEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="admin@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  value={rootAdminName}
                  onChange={(e) => setRootAdminName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Administrator"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSaveRootAdmin}
                  disabled={loading}
                  className="flex items-center gap-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  <Save size={16} />
                  Save
                </button>
                <button
                  onClick={() => {
                    setEditingRootAdmin(false);
                    const rootAdmin = admins.find((admin) => admin.role === 'root');
                    if (rootAdmin) {
                      setRootAdminEmail(rootAdmin.email);
                      setRootAdminName(rootAdmin.displayName);
                    }
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Note: To change your password, update your Google Account password at{' '}
                <a
                  href="https://myaccount.google.com/security"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Google Account Security
                </a>
              </p>
            </div>
          ) : (
            <div className="text-sm text-gray-700">
              {admins.find((admin) => admin.role === 'root') && (
                <>
                  <p><strong>Email:</strong> {admins.find((admin) => admin.role === 'root')?.email}</p>
                  <p><strong>Display Name:</strong> {admins.find((admin) => admin.role === 'root')?.displayName}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    The root admin cannot be deleted and has full access to all admin features.
                  </p>
                </>
              )}
            </div>
          )}
        </div>

      {/* Regular Admins and Moderators Section */}
      <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Administrators & Moderators</h3>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center gap-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <UserPlus size={16} />
              Add User
            </button>
          </div>

          {/* Add Admin Form */}
          {showAddForm && (
            <div className="bg-gray-50 p-4 rounded-lg mb-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={newAdminEmail}
                  onChange={(e) => setNewAdminEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="admin@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name (Optional)
                </label>
                <input
                  type="text"
                  value={newAdminName}
                  onChange={(e) => setNewAdminName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Administrator Name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Module access <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Select which admin sections this user can access. User Management is only available to the root admin.
                </p>
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={selectAllModules}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Select all
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    type="button"
                    onClick={clearAllModules}
                    className="text-xs text-gray-600 hover:text-gray-700 font-medium"
                  >
                    Clear all
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto bg-white">
                  {ASSIGNABLE_ADMIN_MODULES.map((module) => (
                    <label
                      key={module.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded px-2 py-1.5 -mx-2 -my-0.5"
                    >
                      <input
                        type="checkbox"
                        checked={newAdminModules.includes(module.id)}
                        onChange={() => toggleModule(module.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{module.label}</span>
                    </label>
                  ))}
                </div>
                {newAdminModules.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {newAdminModules.length} module{newAdminModules.length !== 1 ? 's' : ''} selected
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAddAdmin}
                  disabled={loading || !newAdminEmail.trim() || newAdminModules.length === 0}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
                >
                  {loading ? 'Adding...' : 'Add User'}
                </button>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewAdminEmail('');
                    setNewAdminName('');
                    setNewAdminModules([]);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
              <p className="text-xs text-gray-500">
                The new admin will need to sign in with Google using this email to access admin features.
              </p>
            </div>
          )}

          {/* Admins and Moderators List */}
          <div className="space-y-2">
            {admins.filter((admin) => admin.role === 'admin' || admin.role === 'moderator').length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No administrators or moderators yet. Click &quot;Add User&quot; to create one.
              </p>
            ) : (
              admins
                .filter((admin) => admin.role === 'admin' || admin.role === 'moderator')
                .map((admin) =>
                  editingAdminId === admin.id ? (
                    /* ── Inline edit form ── */
                    <div
                      key={admin.id}
                      className="border border-blue-200 rounded-lg bg-blue-50 p-4 space-y-3"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-semibold text-blue-800">Editing: {admin.displayName}</p>
                        <button
                          onClick={cancelEditingAdmin}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          aria-label="Cancel editing"
                        >
                          <X size={18} />
                        </button>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Display Name
                        </label>
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                          placeholder="Administrator Name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          value={editEmail}
                          onChange={(e) => setEditEmail(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                          placeholder="admin@example.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Module access <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-2 mb-2">
                          <button
                            type="button"
                            onClick={() => setEditModules(ASSIGNABLE_ADMIN_MODULES.map((m) => m.id))}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                          >
                            Select all
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            type="button"
                            onClick={() => setEditModules([])}
                            className="text-xs text-gray-600 hover:text-gray-700 font-medium"
                          >
                            Clear all
                          </button>
                        </div>
                        <div className="border border-gray-200 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto bg-white">
                          {ASSIGNABLE_ADMIN_MODULES.map((module) => (
                            <label
                              key={module.id}
                              className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded px-2 py-1.5 -mx-2 -my-0.5"
                            >
                              <input
                                type="checkbox"
                                checked={editModules.includes(module.id)}
                                onChange={() => toggleEditModule(module.id)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">{module.label}</span>
                            </label>
                          ))}
                        </div>
                        {editModules.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            {editModules.length} module{editModules.length !== 1 ? 's' : ''} selected
                          </p>
                        )}
                      </div>

                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={() => handleSaveAdmin(admin)}
                          disabled={loading || !editEmail.trim() || editModules.length === 0}
                          className="flex items-center gap-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
                        >
                          <Save size={15} />
                          Save changes
                        </button>
                        <button
                          onClick={cancelEditingAdmin}
                          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* ── Read-only row ── */
                    <div
                      key={admin.id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-medium text-gray-900">{admin.displayName}</h4>
                          <span className={`text-xs px-2 py-1 rounded ${
                            admin.role === 'moderator'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {admin.role === 'moderator' ? 'Moderator' : 'Admin'}
                          </span>
                          {!admin.isActive && (
                            <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                              Inactive
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{admin.email}</p>
                        {admin.allowedModules && admin.allowedModules.length > 0 && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            Access: {admin.allowedModules.map((id) => ASSIGNABLE_ADMIN_MODULES.find((m) => m.id === id)?.label ?? id).join(', ')}
                          </p>
                        )}
                        <p className="text-xs text-gray-400">
                          Created: {admin.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                        </p>
                      </div>
                      <div className="flex gap-2 ml-3 flex-shrink-0">
                        <button
                          onClick={() => startEditingAdmin(admin)}
                          disabled={loading}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Edit admin"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleToggleActive(admin)}
                          disabled={loading}
                          className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                            admin.isActive
                              ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          } disabled:opacity-50`}
                        >
                          {admin.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleDeleteAdmin(admin)}
                          disabled={loading}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Delete admin"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  )
                )
            )}
          </div>
        </div>
    </>
  );

  // If modal mode, wrap in modal container
  if (isModalMode) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full my-8">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Shield className="text-blue-600" size={24} />
              <h2 className="text-2xl font-bold text-gray-900">Admin User Management</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
          {pageContent}
        </div>
      </div>
    );
  }

  // Otherwise, return as page component
  return (
    <div className="w-full">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="text-blue-600" size={28} />
          <h1 className="text-3xl font-bold text-gray-900">Admin User Management</h1>
        </div>
        <p className="text-gray-600">Manage administrators and their access levels</p>
      </div>
      {pageContent}
    </div>
  );
};
